// Form-level error summary. Sits at the top of a form and lists every
// validation problem so a user at the bottom of a long page (e.g. agent edit)
// gets immediate feedback on Save instead of "the button stopped pulsing,
// I have no idea why". Pattern follows GOV.UK + GitHub: a single role="alert"
// region with bullet-list links that scroll-and-focus the offending field.
//
// Mount unconditionally and gate visibility via the `errors` prop — the
// alert role announces additions on its own; remounting the region fights
// the announcement on some screen readers.

import { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils.js';

export interface ErrorSummaryItem {
  // DOM id of the field's input element. Must match what the Field renders
  // so document.getElementById can locate it for scroll + focus.
  fieldId: string;
  label: string;
  message: string;
}

interface ErrorSummaryProps {
  errors: ErrorSummaryItem[];
  // Bumped by the parent each time a submit attempt produces errors. The
  // heading re-focuses on every bump so a second failed submit re-announces
  // (otherwise the second click feels silent).
  focusKey: number;
  className?: string;
}

export function ErrorSummary({ errors, focusKey, className }: ErrorSummaryProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (errors.length === 0) return;
    // Heading has tabIndex=-1 so .focus() works on a non-interactive element.
    // The browser's default focus() scrolls the heading into view; combined
    // with role=alert the screen reader announces the heading + list.
    headingRef.current?.focus();
  }, [focusKey, errors.length]);

  if (errors.length === 0) return null;

  const title =
    errors.length === 1
      ? "There's a problem with this form"
      : `There are ${errors.length.toString()} problems with this form`;

  return (
    <div
      role="alert"
      className={cn(
        'rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm',
        className,
      )}
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-base font-semibold text-destructive focus-visible:outline-none"
      >
        {title}
      </h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 marker:text-destructive">
        {errors.map((err) => (
          <li key={err.fieldId}>
            <button
              type="button"
              onClick={() => {
                focusField(err.fieldId);
              }}
              className="text-left text-destructive underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              <span className="font-medium">{err.label}:</span> {err.message}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Smooth-scroll the field into view, then move focus without re-scrolling
// (`preventScroll: true` so the smooth-scroll animation isn't interrupted by
// the focus's instant-scroll). The scrollIntoView guard is for jsdom, which
// doesn't implement it on Element — production browsers always have it.
function focusField(fieldId: string): void {
  const el = document.getElementById(fieldId);
  if (!el) return;
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  if (el instanceof HTMLElement) {
    el.focus({ preventScroll: true });
  }
}
